<template>

    <!-- Notification box -->
    <div 
        class="notification-box" 
        :class="[
            'notification-box-' + type,
            { 'notification-box-with-message-and-action' : isMessageContainerVisible && isActionContainerVisible },
            brandClass()
        ]">

        <!-- Message container -->
        <div 
            v-if="isMessageContainerVisible" 
            class="notification-box-message-container">

            <!-- Message icon -->
            <div class="notification-box-message-icon" />

            <!-- Message text -->
            <div class="notification-box-message-text">
                {{ $t(messageTextKey) }}
            </div>
        </div>

        <!-- Action container -->
        <div
            v-if="isActionContainerVisible"
            class="notification-box-action-container"
            tabindex="0"
            @keydown="ariaClick"
            role="button"
            @click="$emit('onActionContainerClick')">

            <!-- Action icon -->
            <div class="notification-box-action-icon" />

            <!-- Action text -->
            <div class="notification-box-action-text">
                {{ $t(actionTextKey) }}
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.notification-box {
    background: #F2F2F2;
    padding: 1rem;

    .notification-box-message-container {
        display: inline-flex;

        .notification-box-message-icon {
            background-image: url("~@/assets/default/light/message-info-default-light.svg");
            background-repeat: no-repeat;
            background-size: contain;
            width: 21px;
            height: 21px;
            padding-right: 2rem;
        }

        .notification-box-message-text {
            font-size: 14px;
        }
    }

    &.notification-box-with-message-and-action {
        .notification-box-message-container {
            margin-bottom: 1rem;
        }
    }

    .notification-box-action-container {
        display: inline-flex;
        cursor: pointer;

        .notification-box-action-icon {
            background-image: url("~@/assets/default/light/edit-default-light.svg");
            background-repeat: no-repeat;
            background-size: contain;
            width: 21px;
            height: 21px;
            padding-right: 2rem;
        }

        .notification-box-action-text {
            font-size: 14px;
            font-weight: bold;
        }

        &:hover {
            .notification-box-action-text {
                text-decoration-line: underline;
            }
        }
    }

    &.notification-box-success {
        background: $messagesuccess;

        .notification-box-message-container {
            .notification-box-message-text {
                color: $messagesuccessbg;
            }

            .notification-box-message-icon {
                background-image: url("~@/assets/default/dark/info-default-dark.svg");
            }
        }
        
        .notification-box-action-container {
            .notification-box-action-text {
                color: $messagesuccessbg;
            }

            .notification-box-action-icon {
                background-image: url("~@/assets/default/dark/edit-default-dark.svg");
            }
        }
        
    }

    &.notification-box-error {
        background: $messageerrorbg;

        .notification-box-message-container {
            .notification-box-message-text {
                color: $messageerror;
            }
        }

        .notification-box-action-container {
            .notification-box-action-text {
                color: $messageerror;
            }
        }
    }

    &.notification-box-warning {
        background-color: $messagewarningbg;

        .notification-box-message-container {
            .notification-box-message-text {
                color: $black;
            }
        }

        .notification-box-action-container {
            .notification-box-action-text {
                color: $black;
            }
        }
    }

    &.notification-box-info {
        background: $messageinfobg;

        .notification-box-message-container {
            .notification-box-message-text {
                color: $black;
            }
        }

        .notification-box-action-container {
            .notification-box-action-text {
                color: $black;
            }
        }
    }

    &.__m {     
        .notification-box-message-container {
            .notification-box-message-text {
                font-size: 16px;
                margin-top: 3px;
            }
        }

        .notification-box-action-container {
            .notification-box-action-icon {
                background-image: url("~@/assets/mini/light/edit-mini-light.svg");
            }
        }

        &.notification-box-success {
            background: $messagesuccessbgmini;

            .notification-box-message-container {
                .notification-box-message-icon {
                    background-image: url("~@/assets/mini/light/message-info-mini-light.svg");
                }

                .notification-box-message-text {
                    color: $messagesuccessmini;
                }
            }
            
            .notification-box-action-container {
                .notification-box-action-text {
                    color: $messagesuccessmini;
                }
            }
            
        }

        &.notification-box-error {
            background: $messageerrorbgmini;

            .notification-box-message-container {
                .notification-box-message-text {
                    color: $messageerrormini;
                }
            }

            .notification-box-action-container {
                .notification-box-action-text {
                    color: $messageerrormini;
                }
            }
        }

        &.notification-box-info {
            background: $messageinfobg;

            .notification-box-message-container {
                .notification-box-message-text {
                    color: $black;
                }
            }

            .notification-box-action-container {
                .notification-box-action-text {
                    color: $black;
                }
            }
        }
    }

    &.__rr {
        .notification-box-message-container {
            .notification-box-message-text {
                font-family: RivieraNights-Light;
            }
        }

        .notification-box-action-container {
            .notification-box-action-text {
                font-family: RivieraNights-Regular;
                font-weight: normal;
            }
        }
    }

    &.__d {
        .notification-box-message-container {
            .notification-box-message-text {
                color: $black;
            }
        }

        .notification-box-action-container {
            .notification-box-action-text {
                color: $black;
            }
        }
    }
}
</style>

<script>
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";

export default {
    name: "NotificationBox",
    inject: [
        "$c", 
        "$t", 
        "brandClass"
    ],
    mixins: [
        oneUtils.accessibilityMixin
    ],
    emits: [
        "onActionContainerClick"
    ],
    props: {
        type: {
            type: String,
            required: false,
            default: "info"
        },
        messageTextKey: {
            type: String,
            required: false,
            default: ""
        },
        actionTextKey: {
            type: String,
            required: false,
            default: ""
        },
    },
    computed: {
        isMessageContainerVisible() {
            return this.messageTextKey;
        },
        isActionContainerVisible() {
            return this.actionTextKey;
        }
    }
};
</script>

